import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Checkbox from "@cloudscape-design/components/checkbox"
import Container from "@cloudscape-design/components/container"
import Modal from "@cloudscape-design/components/modal"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { useState } from "react"
import { DQDL_RULES } from "./constants"
import { DqdlRule } from "./types"

/**
 * A suggested rule with AI metadata for display in the review modal.
 */
interface SuggestionRule extends DqdlRule {
  reasoning?: string | null
  confidence?: number | null
}

/**
 * Suggestions data structure from the API.
 */
interface SuggestionsData {
  majorRules: SuggestionRule[]
  minorRules: SuggestionRule[]
}

interface SuggestionsReviewModalProps {
  suggestions: SuggestionsData
  onApply: (majorRules: DqdlRule[], minorRules: DqdlRule[]) => void
  onCancel: () => void
}

const getConfidenceColor = (confidence: number): "green" | "blue" | "red" => {
  if (confidence >= 0.8) return "green"
  if (confidence >= 0.5) return "blue"
  return "red"
}

const getConfidenceLabel = (confidence: number): string => {
  if (confidence >= 0.8) return "High"
  if (confidence >= 0.5) return "Medium"
  return "Low"
}

const getRuleDescription = (rule: SuggestionRule): string => {
  switch (rule.type) {
    case "IsComplete":
      if (rule.where) {
        return `\`${rule.column}\` must not be empty when \`${rule.where}\``
      }
      return `\`${rule.column}\` must not be empty`
    case "IsPrimaryKey":
      return `\`${(rule.columns || []).join(", ")}\` must be unique`
    case "ColumnValues":
      return `\`${rule.column}\` must match \`${rule.pattern}\``
    case "CustomSql":
      return "Custom SQL validation"
    case "DataFreshness":
      return `\`${rule.column}\` must be within ${rule.days} days`
    case "ColumnLength":
      return `\`${rule.column}\` length must be ${rule.min}–${rule.max ?? "∞"}`
    case "Completeness":
      return `\`${rule.column}\` must be ≥${Math.round(parseFloat(rule.threshold ?? "0") * 100)}% complete (non-null)`
    case "DistinctValuesCount":
      return `\`${rule.column}\` must have ≥${rule.min} distinct values`
    default:
      return rule.type
  }
}

/**
 * Renders a rule description, wrapping any backtick-delimited segments as inline code.
 */
const renderDescription = (description: string): React.ReactNode =>
  description.split("`").map((part, i) =>
    i % 2 === 1 ? (
      <Box key={i} variant="code" display="inline">
        {part}
      </Box>
    ) : (
      part
    ),
  )

/**
 * Strips AI metadata (reasoning, confidence) from a suggestion rule, returning a plain DqdlRule
 * suitable for form state and persistence.
 */
function stripAiMetadata(suggestion: SuggestionRule): DqdlRule {
  const { reasoning, confidence, ...rule } = suggestion
  return rule
}

/**
 * Modal for reviewing AI-generated DQDL rule suggestions. Displays rules with confidence badges and
 * reasoning, allowing users to selectively apply suggestions to the form.
 */
export const SuggestionsReviewModal: React.FC<SuggestionsReviewModalProps> = ({
  suggestions,
  onApply,
  onCancel,
}) => {
  // State - track selected indices for each section
  const [selectedMajor, setSelectedMajor] = useState<Set<number>>(
    () =>
      new Set(
        suggestions.majorRules
          .map((r, i) => (!(r as DqdlRule & { _added?: boolean })._added ? i : -1))
          .filter((i) => i >= 0),
      ),
  )
  const [selectedMinor, setSelectedMinor] = useState<Set<number>>(
    () =>
      new Set(
        suggestions.minorRules
          .map((r, i) => (!(r as DqdlRule & { _added?: boolean })._added ? i : -1))
          .filter((i) => i >= 0),
      ),
  )

  // Derived values
  const totalSelected = selectedMajor.size + selectedMinor.size
  const totalRules = suggestions.majorRules.length + suggestions.minorRules.length

  // Handlers
  const toggleRule = (section: "major" | "minor", index: number) => {
    const setter = section === "major" ? setSelectedMajor : setSelectedMinor
    setter((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  const toggleSelectAll = (section: "major" | "minor") => {
    const rules = section === "major" ? suggestions.majorRules : suggestions.minorRules
    const selected = section === "major" ? selectedMajor : selectedMinor
    const setter = section === "major" ? setSelectedMajor : setSelectedMinor

    if (selected.size === rules.length) {
      setter(new Set())
    } else {
      setter(new Set(rules.map((_, i) => i)))
    }
  }

  const handleApply = () => {
    const majorRules = suggestions.majorRules
      .filter(
        (_, i) =>
          selectedMajor.has(i) &&
          !(suggestions.majorRules[i] as DqdlRule & { _added?: boolean })._added,
      )
      .map(stripAiMetadata)
    const minorRules = suggestions.minorRules
      .filter(
        (_, i) =>
          selectedMinor.has(i) &&
          !(suggestions.minorRules[i] as DqdlRule & { _added?: boolean })._added,
      )
      .map(stripAiMetadata)
    onApply(majorRules, minorRules)
  }

  const renderRuleItem = (rule: SuggestionRule, index: number, section: "major" | "minor") => {
    const selected = section === "major" ? selectedMajor : selectedMinor
    const isAdded = (rule as DqdlRule & { _added?: boolean })._added
    const ruleLabel = DQDL_RULES[rule.type]?.label || rule.type
    const ruleTarget = rule.column ?? (rule.columns || []).join(", ") ?? ""
    const checkboxLabel = ruleTarget ? `Select ${ruleLabel}: ${ruleTarget}` : `Select ${ruleLabel}`

    return (
      <Container key={`${section}-${index}`}>
        <SpaceBetween size="xs" direction="horizontal">
          <Checkbox
            checked={!!isAdded || selected.has(index)}
            disabled={!!isAdded}
            onChange={() => toggleRule(section, index)}
            ariaLabel={checkboxLabel}
          />
          <SpaceBetween size="xs">
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
              <Box variant="strong">{ruleLabel}</Box>
              {isAdded && <Badge color="green">Already added</Badge>}
              {rule.confidence != null && (
                <Badge color={getConfidenceColor(rule.confidence)}>
                  {getConfidenceLabel(rule.confidence)}
                </Badge>
              )}
            </SpaceBetween>

            <Box variant="small">{renderDescription(getRuleDescription(rule))}</Box>

            {rule.reasoning && (
              <Box variant="small" color="text-body-secondary" fontSize="body-s">
                {rule.reasoning}
              </Box>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Container>
    )
  }

  const renderSection = (
    title: string,
    rules: SuggestionRule[],
    selected: Set<number>,
    section: "major" | "minor",
  ) => {
    if (rules.length === 0) return null

    return (
      <SpaceBetween size="s">
        <SpaceBetween size="xs" direction="horizontal" alignItems="center">
          <Box variant="h4" padding="n">
            {title}
          </Box>
          <Button variant="inline-link" onClick={() => toggleSelectAll(section)}>
            {selected.size === rules.length ? "Deselect all" : "Select all"}
          </Button>
        </SpaceBetween>
        <SpaceBetween size="xs">
          {rules.map((rule, i) => renderRuleItem(rule, i, section))}
        </SpaceBetween>
      </SpaceBetween>
    )
  }

  return (
    <Modal
      visible
      onDismiss={onCancel}
      size="large"
      header="Review AI Suggestions"
      footer={
        <Box float="right">
          <SpaceBetween size="s" direction="horizontal" alignItems="center">
            <Box variant="small" color="text-body-secondary">
              {totalSelected} of {totalRules} rules selected
            </Box>
            <Button variant="link" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="primary" disabled={totalSelected === 0} onClick={handleApply}>
              Add {totalSelected} Rule{totalSelected !== 1 ? "s" : ""}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        <Box color="text-body-secondary">
          Select which rules to add to your data quality configuration
        </Box>
        {renderSection("Major Rules", suggestions.majorRules, selectedMajor, "major")}
        {renderSection("Minor Rules", suggestions.minorRules, selectedMinor, "minor")}
      </SpaceBetween>
    </Modal>
  )
}
